import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import { ArrowBackIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';
import HeaderMenus from '../HeaderMenus';

import VarianceDetailsCard from './VarianceDetailsCard';
import VarianceIcd10Card from './VarianceIcd10Card';
import VarianceNotesSection from './VarianceNotesSection';
import VarianceDocumentViewer from './VarianceDocumentViewer';
import ModernChat from '../ModernChat';
import JudgementCallSection from './JudgementCallSection';

const MPHCodingVarianceReview = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useContext(AppContext);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [caseFiles, setCaseFiles] = useState([]);

  const location = useLocation();
  const caseList = location.state?.caseList || [];
  
  const currentIndex = caseList.indexOf(caseId);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex !== -1 && currentIndex < caseList.length - 1;
  
  const prevCaseId = hasPrev ? caseList[currentIndex - 1] : null;
  const nextCaseId = hasNext ? caseList[currentIndex + 1] : null;

  const handleNavigate = (targetId) => {
    navigate(`/mph-coding-variance/${targetId}`, { state: { caseList } });
  };


  useEffect(() => {
    const fetchReviewData = async () => {
      if (!caseId || !accessToken) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // 1. Fetch the variance report and feedback
        const reportResponse = await callApi(`/api/mph/coding-variance/${caseId}`, accessToken);
        if (!reportResponse.ok) {
          throw new Error('Failed to fetch variance report');
        }
        const reportResult = await reportResponse.json();
        if (reportResult.status === 'success') {
          setReportData(reportResult.data);
        } else {
          throw new Error(reportResult.message || 'Failed to fetch variance report');
        }

        // 2. Fetch the case files for the document viewer
        const detailsResponse = await callApi(`/get_case_details/${caseId}`, accessToken);
        if (detailsResponse.ok) {
          const detailsData = await detailsResponse.json();
          setCaseFiles(detailsData.files || []);
        }
        
      } catch (err) {
        console.error("Error fetching review data:", err);
        setError(err.message || "An error occurred while loading the review page.");
      } finally {
        setLoading(false);
      }
    };

    fetchReviewData();
  }, [caseId, accessToken]);

  return (
    <Box>
      <HeaderMenus />
      <Box p={4} maxWidth="1400px" margin="0 auto">
        
        <Flex justify="space-between" align="center" mb={4}>
          <Button 
            leftIcon={<ArrowBackIcon />} 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/mph-coding-variance-dashboard')}
          >
            Back to Dashboard
          </Button>
          
          {caseList.length > 0 && (
            <HStack spacing={2}>
              <Text fontSize="sm" color="gray.500" mr={2}>
                Case {currentIndex + 1} of {caseList.length}
              </Text>
              <Button 
                leftIcon={<ChevronLeftIcon />} 
                size="sm" 
                variant="outline"
                isDisabled={!hasPrev}
                onClick={() => handleNavigate(prevCaseId)}
              >
                Previous
              </Button>
              <Button 
                rightIcon={<ChevronRightIcon />} 
                size="sm" 
                variant="outline"
                isDisabled={!hasNext}
                onClick={() => handleNavigate(nextCaseId)}
              >
                Next
              </Button>
            </HStack>
          )}
        </Flex>

        {error && (
          <Alert status="error" mb={4} borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {loading ? (
          <Flex justify="center" align="center" p={20}>
            <VStack spacing={4}>
              <Spinner size="xl" color="blue.500" thickness="4px" />
              <Text>Loading case details...</Text>
            </VStack>
          </Flex>
        ) : reportData ? (
          <Box>
            {/* Judgement Call Section (Only renders if questions exist) */}
            <JudgementCallSection 
              reportData={reportData} 
              accessToken={accessToken} 
              onSaveSuccess={() => {
                if (hasNext) {
                  handleNavigate(nextCaseId);
                }
              }}
            />

            {/* Top: Reviewer Notes */}
            <VarianceNotesSection 
              caseId={caseId} 
              initialNotes={reportData.feedback_notes} 
              accessToken={accessToken} 
            />

            <Tabs variant="enclosed" colorScheme="blue" mt={6}>
              <TabList>
                <Tab fontWeight="medium">Variance Details</Tab>
                <Tab fontWeight="medium">AI Chat</Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0} py={4}>
                  <VarianceDetailsCard report={reportData} />
                  <VarianceIcd10Card report={reportData} />
                  <VarianceDocumentViewer 
                    caseId={caseId} 
                    caseFiles={caseFiles} 
                    accessToken={accessToken} 
                  />
                </TabPanel>

                <TabPanel px={0} py={4}>
                  <Box h="600px">
                    <ModernChat 
                      caseId={caseId} 
                      contextType="coding_variance" 
                      height="600px"
                    />
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        ) : null}

      </Box>
    </Box>
  );
};

export default MPHCodingVarianceReview;
